import { useParams, useNavigate } from 'react-router-dom'
import { useTenantDetail } from '@/features/tenants/hooks/useTenantDetail'
import { TenantOrganizationsTab } from '@/features/tenants/components/TenantOrganizationsTab'
import { TenantUsageTab } from '@/features/tenants/components/TenantUsageTab'
import { TenantSettingsTab } from '@/features/tenants/components/TenantSettingsTab'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Layers, ArrowLeft, Loader2, Building2, BarChart3, Settings } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'

export default function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()

  const { data: tenant, isLoading, error } = useTenantDetail(tenantId || '')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !tenant) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Tenant Not Found"
          description="The tenant you're looking for doesn't exist"
          icon={<Layers className="h-6 w-6" />}
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error ? 'Failed to load tenant' : 'Tenant not found'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/tenants')} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tenants
        </Button>
      </div>
    )
  }

  const getStatusBadge = () => {
    if (tenant.isDeleted) {
      return <Badge variant="destructive">Deleted</Badge>
    }
    if (!tenant.isActive) {
      return <Badge variant="outline">Inactive</Badge>
    }
    return <Badge variant="default">Active</Badge>
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tenants')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <PageHeader
            title={tenant.displayName}
            description={`Tenant details and management`}
            icon={<Layers className="h-6 w-6" />}
          />
        </div>
      </div>

      {/* Overview Card */}
      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
          <CardDescription>Basic tenant information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Name</p>
              <p className="text-sm">{tenant.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Status</p>
              <div className="mt-1">{getStatusBadge()}</div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Created</p>
              <p className="text-sm">{new Date(tenant.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="organizations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="organizations">
            <Building2 className="mr-2 h-4 w-4" />
            Organizations
          </TabsTrigger>
          <TabsTrigger value="usage">
            <BarChart3 className="mr-2 h-4 w-4" />
            Usage
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>
        <TabsContent value="organizations">
          <TenantOrganizationsTab tenantId={tenant._id} tenantName={tenant.displayName} />
        </TabsContent>
        <TabsContent value="usage">
          <TenantUsageTab tenantId={tenant._id} />
        </TabsContent>
        <TabsContent value="settings">
          <TenantSettingsTab tenant={tenant} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
